import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  emailsApi,
  llmApi,
  signaturenApi,
  mailKontenApi,
  textbausteineApi,
  adressenApi,
  ticketsApi,
  dokumenteApi,
} from '../../services/api';
import kalenderService from '../../services/kalenderService';
import type { Email, EmailAnhang, TicketAnalysisResponse } from '../../types/api';
import ArchivDialog from '../../components/ArchivDialog';

// Local components
import EmailTable from './EmailTable';
import EmailDetailsDialog from './EmailDetailsDialog';
import EmailTicketDialog from './EmailTicketDialog';
import EmailAiReplyDialog from './EmailAiReplyDialog';
import EmailAppointmentDialog from './EmailAppointmentDialog';

// Utils and types
import { createCalendarEvent } from './utils';
import type { TicketFormData, AiReplySuggestion, ExtractedAppointment, MailKonto } from './types';

const Emails: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Core state
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetching, setFetching] = useState(false);

  // Data state
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [attachments, setAttachments] = useState<EmailAnhang[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [signaturen, setSignaturen] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [textbausteine, setTextbausteine] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [adressen, setAdressen] = useState<any[]>([]);
  const [mailKonten, setMailKonten] = useState<MailKonto[]>([]);

  // Dialog states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [aiReplySuggestOpen, setAiReplySuggestOpen] = useState(false);
  const [appointmentExtractOpen, setAppointmentExtractOpen] = useState(false);
  const [archivDialogOpen, setArchivDialogOpen] = useState(false);

  // Loading states
  const [ticketAnalyzing, setTicketAnalyzing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [aiReplySuggesting, setAiReplySuggesting] = useState(false);
  const [appointmentExtracting, setAppointmentExtracting] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [savingToCalendar, setSavingToCalendar] = useState(false);

  // Dialog data
  const [ticketAnalysis, setTicketAnalysis] = useState<TicketAnalysisResponse | null>(null);
  const [ticketFormData, setTicketFormData] = useState<TicketFormData>({
    titel: '',
    was: '',
    wie: '',
    wann: '',
    prioritaet: 'Mittel',
    status: 'Offen',
  });
  const [aiReplySuggestions, setAiReplySuggestions] = useState<AiReplySuggestion[]>([]);
  const [extractedAppointments, setExtractedAppointments] = useState<ExtractedAppointment[]>([]);
  const [appointmentSummary, setAppointmentSummary] = useState<string | null>(null);
  const [archivEmailId, setArchivEmailId] = useState<number | null>(null);
  const [archivEmailTitle, setArchivEmailTitle] = useState('');

  // CalDAV


  // Data loading
  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailsApi.getAll({ take: 100 });
      setEmails(response.data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('E-Mail-Fehler:', err);
      setError(error.response?.data?.message || 'Fehler beim Laden der E-Mails');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSignaturen = useCallback(async () => {
    try {
      const response = await signaturenApi.getAll();
      setSignaturen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Signaturen:', err);
    }
  }, []);

  const loadTextbausteine = useCallback(async () => {
    try {
      const response = await textbausteineApi.getAll();
      setTextbausteine(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Textbausteine:', err);
    }
  }, []);

  const loadAdressen = useCallback(async () => {
    try {
      const response = await adressenApi.getAll();
      setAdressen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Adressen:', err);
    }
  }, []);

  const loadMailKonten = useCallback(async () => {
    try {
      const response = await mailKontenApi.getAll();
      setMailKonten(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Mail-Konten:', err);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadEmails();
    loadSignaturen();
    loadTextbausteine();
    loadAdressen();
    loadMailKonten();
  }, [loadEmails, loadSignaturen, loadTextbausteine, loadAdressen, loadMailKonten]);

  // Auto-open email from query parameter
  useEffect(() => {
    const emailId = searchParams.get('id');
    const shouldReply = searchParams.get('reply');

    if (emailId && emails.length > 0 && !loading) {
      const email = emails.find(e => e.emailID === parseInt(emailId));
      if (email) {
        handleShowDetails(email);

        if (shouldReply === 'true') {
          setTimeout(() => {
            handleOpenReply(email);
          }, 500);
        }

        setSearchParams({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emails, loading, searchParams, setSearchParams]);

  // Handlers
  const handleFetchEmails = async () => {
    try {
      setFetching(true);
      const response = await mailKontenApi.fetchAllEmails(50);
      alert(response.data.message);
      await loadEmails();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim E-Mail-Abruf:', err);
      alert('Fehler beim E-Mail-Abruf: ' + (error.response?.data?.message || error.message));
    } finally {
      setFetching(false);
    }
  };

  const handleMarkAsRead = async (email: Email, read: boolean) => {
    try {
      await emailsApi.markAsRead(email.emailID, read);
      setEmails(emails.map((e) =>
        e.emailID === email.emailID ? { ...e, gelesen: read } : e
      ));
    } catch {
      alert('Fehler beim Markieren der E-Mail');
    }
  };

  const handleDelete = async (emailId: number) => {
    if (!window.confirm('E-Mail wirklich löschen?')) return;

    try {
      await emailsApi.delete(emailId);
      setEmails(emails.filter((e) => e.emailID !== emailId));
    } catch {
      alert('Fehler beim Löschen der E-Mail');
    }
  };

  const handleDeleteMultiple = async (emailIds: number[]) => {
    if (emailIds.length === 0) return;

    try {
      // Lösche alle ausgewählten E-Mails
      await Promise.all(emailIds.map((id) => emailsApi.delete(id)));
      setEmails(emails.filter((e) => !emailIds.includes(e.emailID)));
    } catch {
      alert('Fehler beim Löschen der E-Mails');
    }
  };

  const handleShowDetails = async (email: Email) => {
    setSelectedEmail(email);
    setDetailsOpen(true);

    if (email.hasAttachments) {
      try {
        const response = await emailsApi.getAttachments(email.emailID);
        setAttachments(response.data);
      } catch (err) {
        console.error('Fehler beim Laden der Anhänge:', err);
      }
    } else {
      setAttachments([]);
    }

    if (!email.gelesen) {
      handleMarkAsRead(email, true);
    }
  };

  const handleOpenReply = (email: Email) => {
    navigate(`/emails/compose/${email.emailID}?mode=reply`);
  };

  const handleOpenForward = (email: Email) => {
    navigate(`/emails/compose/${email.emailID}?mode=forward`);
  };

  const handleOpenNewEmail = () => {
    navigate('/emails/compose/new');
  };

  const handleDownloadAttachment = async (anhangId: number, dateiName: string) => {
    try {
      const response = await emailsApi.downloadAttachment(anhangId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dateiName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler beim Herunterladen des Anhangs: ' + (error.response?.data?.message || error.message));
    }
  };

  // Ticket handlers
  const handleCreateTicketClick = async (email: Email) => {
    try {
      setTicketAnalyzing(true);
      const response = await llmApi.analyzeEmailForTicket({
        emailID: email.emailID,
        betreff: email.betreffText,
        textInhalt: email.textInhalt || '',
        absenderName: email.absenderName,
      });

      setTicketAnalysis(response.data);

      if (response.data.sollTicketErstellen) {
        setTicketFormData({
          titel: response.data.titel || email.betreffText,
          was: response.data.was || '',
          wie: response.data.wie || '',
          wann: response.data.wann || '',
          prioritaet: response.data.prioritaet || 'Mittel',
          status: 'Offen',
        });
      } else {
        alert(`KI-Analyse: ${response.data.grund}\n\nMöchten Sie trotzdem ein Ticket erstellen?`);
        setTicketFormData({
          titel: email.betreffText,
          was: '',
          wie: '',
          wann: '',
          prioritaet: 'Mittel',
          status: 'Offen',
        });
      }
      setTicketDialogOpen(true);
    } catch (err) {
      console.error('Fehler bei Ticket-Analyse:', err);
      alert('Fehler bei der KI-Analyse. Ticket-Dialog wird trotzdem geöffnet.');
      setTicketFormData({
        titel: selectedEmail?.betreffText || '',
        was: '',
        wie: '',
        wann: '',
        prioritaet: 'Mittel',
        status: 'Offen',
      });
      setTicketDialogOpen(true);
    } finally {
      setTicketAnalyzing(false);
    }
  };

  const handleConfirmTicket = async () => {
    if (!selectedEmail) return;

    if (!ticketFormData.titel.trim() || !ticketFormData.was.trim()) {
      alert('Bitte füllen Sie mindestens Titel und Beschreibung aus');
      return;
    }

    const savedFormData = { ...ticketFormData };

    try {
      setCreatingTicket(true);
      await ticketsApi.create({
        titel: ticketFormData.titel,
        was: ticketFormData.was,
        wie: ticketFormData.wie || undefined,
        wann: ticketFormData.wann || undefined,
        status: ticketFormData.status,
        prioritaet: ticketFormData.prioritaet,
        emailID: selectedEmail.emailID,
      });

      setTicketDialogOpen(false);

      if (savedFormData.wann) {
        const createCalendar = window.confirm(
          'Ticket erfolgreich erstellt!\n\n' +
          'Es wurde ein Fälligkeitsdatum erkannt. Möchten Sie einen Kalendereintrag (ICS-Datei) herunterladen?\n\n' +
          `Datum: ${new Date(savedFormData.wann).toLocaleString('de-DE')}\n` +
          `Aufgabe: ${savedFormData.titel}`
        );

        if (createCalendar) {
          createCalendarEvent(
            savedFormData.titel,
            savedFormData.was,
            savedFormData.wann,
            savedFormData.wie
          );
        }
      } else {
        alert('Ticket erfolgreich erstellt!');
      }

      setTicketFormData({
        titel: '',
        was: '',
        wie: '',
        wann: '',
        prioritaet: 'Mittel',
        status: 'Offen',
      });
      setTicketAnalysis(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler beim Erstellen des Tickets: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingTicket(false);
    }
  };

  // AI Summary handler
  const handleCreateAISummary = async (email: Email) => {
    try {
      setSummarizing(true);

      const emailContent = email.htmlInhalt || email.textInhalt || '';
      const prompt = `Erstelle eine sehr detaillierte und ausführliche Zusammenfassung der folgenden E-Mail. Die Zusammenfassung soll umfassend sein und alle wichtigen Informationen enthalten:

Betreff: ${email.betreffText}
Von: ${email.absenderName} <${email.absenderEmail}>
Empfangen am: ${new Date(email.empfangenAm).toLocaleString('de-DE')}

E-Mail-Inhalt:
${emailContent}

Bitte erstelle eine detaillierte Zusammenfassung mit folgenden Abschnitten:

1. ÜBERBLICK: Kurze Einleitung über den Zweck der E-Mail
2. HAUPTINHALT: Ausführliche Beschreibung aller wichtigen Punkte und Details
3. ANLIEGEN/ANFRAGEN: Alle Fragen, Bitten oder Anforderungen die gestellt werden
4. FRISTEN/TERMINE: Wichtige Zeitangaben oder Deadlines
5. NÄCHSTE SCHRITTE: Welche Aktionen werden erwartet oder vorgeschlagen
6. ZUSÄTZLICHE INFORMATIONEN: Weitere relevante Details oder Kontext

Die Zusammenfassung soll sehr genau und umfassend sein.`;

      const response = await llmApi.generate({
        prompt: prompt,
        systemPrompt: 'Du bist ein professioneller Assistent für E-Mail-Analyse. Erstelle sehr detaillierte, präzise und gut strukturierte Zusammenfassungen. Sei gründlich und lasse keine wichtigen Informationen aus.',
      });

      const summaryText = response.data.text;
      const blob = new Blob([summaryText], { type: 'text/plain' });
      const file = new File([blob], `KI-Zusammenfassung_${email.betreffText}.txt`, { type: 'text/plain' });

      await dokumenteApi.upload(file, `KI-Zusammenfassung: ${email.betreffText}`, 'Zusammenfassung');

      alert('Detaillierte KI-Zusammenfassung wurde erfolgreich als Dokument gespeichert!');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler beim Erstellen der KI-Zusammenfassung: ' + (error.response?.data?.message || error.message));
    } finally {
      setSummarizing(false);
    }
  };

  // AI Reply handler
  const handleAiReplySuggest = async (email: Email) => {
    try {
      setAiReplySuggesting(true);
      setAiReplySuggestOpen(true);
      setAiReplySuggestions([]);

      const response = await emailsApi.suggestReply(email.emailID, {});

      if (response.data?.antwortvorschlaege) {
        setAiReplySuggestions(response.data.antwortvorschlaege);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError('Fehler beim Generieren der KI-Antwortvorschläge: ' + (error.response?.data?.message || error.message));
      setAiReplySuggestOpen(false);
    } finally {
      setAiReplySuggesting(false);
    }
  };

  const handleUseSuggestion = (suggestion: AiReplySuggestion) => {
    if (!selectedEmail) return;
    localStorage.setItem('emailAiSuggestion', suggestion.text);
    setAiReplySuggestOpen(false);
    setDetailsOpen(false);
    handleOpenReply(selectedEmail);
  };

  // Appointment handlers
  const handleExtractAppointments = async (email: Email) => {
    try {
      setAppointmentExtracting(true);
      setAppointmentExtractOpen(true);
      setExtractedAppointments([]);
      setAppointmentSummary(null);

      const response = await emailsApi.extractAppointments(email.emailID, {
        autoCreateEvents: false,
      });

      if (response.data?.termine && response.data.termine.length > 0) {
        setExtractedAppointments(response.data.termine);
      }

      if (response.data?.zusammenfassung) {
        setAppointmentSummary(response.data.zusammenfassung);
      }

      if (response.data?.rohAntwort) {
        setAppointmentSummary(response.data.rohAntwort);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError('Fehler beim Extrahieren der Termine: ' + (error.response?.data?.message || error.message));
      setAppointmentExtractOpen(false);
    } finally {
      setAppointmentExtracting(false);
    }
  };

  const handleDownloadAppointmentIcs = (appointment: ExtractedAppointment) => {
    let dateTime = appointment.datum;
    if (appointment.uhrzeit) {
      dateTime = `${appointment.datum}T${appointment.uhrzeit}:00`;
    }
    createCalendarEvent(appointment.titel, appointment.beschreibung || '', dateTime, appointment.ort || '');
  };

  const handleSaveAppointmentToCalendar = async (appointment: ExtractedAppointment) => {
    try {
      setSavingToCalendar(true);

      let startDate: Date;
      let endDate: Date;
      const isAllDay = appointment.istGanztaegig || !appointment.uhrzeit;

      if (appointment.datum) {
        let datumStr = appointment.datum.trim();

        // Konvertiere deutsches Format (TT.MM.JJJJ) zu ISO-Format (JJJJ-MM-TT)
        if (datumStr.includes('.')) {
          const parts = datumStr.split('.');
          if (parts.length === 3) {
            datumStr = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          }
        }

        // Parse das Datum
        const dateParts = datumStr.split('-').map(Number);
        if (dateParts.length !== 3 || dateParts.some(isNaN)) {
          alert('Ungültiges Datumsformat: ' + appointment.datum);
          return;
        }
        const [year, month, day] = dateParts;

        if (appointment.uhrzeit && appointment.uhrzeit.trim()) {
          // Uhrzeit vorhanden - parse sie
          let uhrzeitStr = appointment.uhrzeit.trim();
          // Entferne eventuelle "Uhr" Suffix
          uhrzeitStr = uhrzeitStr.replace(/\s*Uhr\s*/i, '').trim();

          const timeParts = uhrzeitStr.split(':').map(Number);
          if (timeParts.length >= 2 && !timeParts.some(isNaN)) {
            const hours = timeParts[0];
            const minutes = timeParts[1];
            const seconds = timeParts[2] || 0;
            startDate = new Date(year, month - 1, day, hours, minutes, seconds);
          } else {
            // Uhrzeit ungültig, verwende nur das Datum
            startDate = new Date(year, month - 1, day, 0, 0, 0);
          }
        } else {
          // Keine Uhrzeit - ganztägiger Termin
          startDate = new Date(year, month - 1, day, 0, 0, 0);
        }

        if (isNaN(startDate.getTime())) {
          alert('Das Datum konnte nicht geparst werden: ' + appointment.datum + ' ' + (appointment.uhrzeit || ''));
          return;
        }
      } else {
        alert('Kein Datum im Termin gefunden.');
        return;
      }

      const durationMinutes = appointment.dauerMinuten || 60;
      if (isAllDay) {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      } else {
        endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      }

      // Verwende lokalen Kalender statt CalDAV
      const kalenderRequest = {
        kalenderID: 0, // 0 = automatisch Aufgaben-Kalender finden/erstellen
        titel: appointment.titel || 'Termin',
        beschreibung: appointment.beschreibung || '',
        ort: appointment.ort || '',
        startDatum: startDate.toISOString(),
        endDatum: endDate.toISOString(),
        ganztaegig: isAllDay,
      };

      const ereignis = await kalenderService.createEreignis(kalenderRequest);

      if (ereignis && ereignis.ereignisID) {
        alert('Termin erfolgreich im lokalen Kalender erstellt!');
      } else {
        alert('Termin konnte nicht erstellt werden.');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler beim Speichern des Termins: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingToCalendar(false);
    }
  };

  // Archive handler
  const handleOpenArchive = (email: Email) => {
    setArchivEmailId(email.emailID);
    setArchivEmailTitle(email.betreffText);
    setArchivDialogOpen(true);
  };

  // Filter emails
  const filteredEmails = emails.filter(
    (email) =>
      email.betreffText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.absenderAnzeige.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">E-Mails</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenNewEmail}
          >
            Neue E-Mail
          </Button>
          <TextField
            size="small"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Neue E-Mails abrufen">
            <IconButton
              onClick={handleFetchEmails}
              color="primary"
              disabled={fetching}
            >
              {fetching ? <CircularProgress size={24} /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadEmails} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Email Table */}
      <EmailTable
        emails={filteredEmails}
        mailKonten={mailKonten}
        onShowDetails={handleShowDetails}
        onMarkAsRead={handleMarkAsRead}
        onReply={handleOpenReply}
        onArchive={handleOpenArchive}
        onDelete={handleDelete}
        onDeleteMultiple={handleDeleteMultiple}
      />

      {/* Dialogs */}
      <EmailDetailsDialog
        open={detailsOpen}
        email={selectedEmail}
        attachments={attachments}
        onClose={() => setDetailsOpen(false)}
        onReply={handleOpenReply}
        onForward={handleOpenForward}
        onCreateTicket={handleCreateTicketClick}
        onCreateSummary={handleCreateAISummary}
        onAiReply={handleAiReplySuggest}
        onExtractAppointments={handleExtractAppointments}
        onDownloadAttachment={handleDownloadAttachment}
        ticketAnalyzing={ticketAnalyzing}
        summarizing={summarizing}
        aiReplySuggesting={aiReplySuggesting}
        appointmentExtracting={appointmentExtracting}
      />

      <EmailTicketDialog
        open={ticketDialogOpen}
        email={selectedEmail}
        analysis={ticketAnalysis}
        onClose={() => setTicketDialogOpen(false)}
        onConfirm={handleConfirmTicket}
        formData={ticketFormData}
        onFormChange={(data) => setTicketFormData({ ...ticketFormData, ...data })}
        creating={creatingTicket}
      />

      <EmailAiReplyDialog
        open={aiReplySuggestOpen}
        suggestions={aiReplySuggestions}
        loading={aiReplySuggesting}
        onClose={() => setAiReplySuggestOpen(false)}
        onUseSuggestion={handleUseSuggestion}
      />

      <EmailAppointmentDialog
        open={appointmentExtractOpen}
        appointments={extractedAppointments}
        summary={appointmentSummary}
        loading={appointmentExtracting}
        savingToCalendar={savingToCalendar}
        onClose={() => setAppointmentExtractOpen(false)}
        onDownloadIcs={handleDownloadAppointmentIcs}
        onSaveToCalendar={handleSaveAppointmentToCalendar}
      />

      <ArchivDialog
        open={archivDialogOpen}
        onClose={() => setArchivDialogOpen(false)}
        sourceType="email"
        sourceId={archivEmailId || 0}
        defaultTitle={archivEmailTitle}
      />
    </Box>
  );
};

export default Emails;
